import { useQuery } from '@apollo/client'
import { Badge, Loader, Timeline } from '@mantine/core'
import dayjs from 'dayjs'

import DnDrawer from 'src/components/Core/DnDrawer'
import { GET_API_CLIENT_ACCESS_HISTORY } from 'src/graphql/apiClientAccesses.graphql'

export type ApiClientAccessHistoryRow = {
  clientId: string
  clientName: string | null
  company: { companyId: string; companyName: string }
}

type HistoryEntry = {
  auditTrailId: string
  createdAt: string
  changeType: 'CREATE' | 'UPDATE' | 'DELETE'
  user: { userId: string; firstName: string; lastName: string } | null
  prevScopes: string[]
  newScopes: string[]
  prevActive: boolean | null
  newActive: boolean | null
}

type ApiClientAccessHistoryDrawerProps = {
  row: ApiClientAccessHistoryRow | null
  onClose: () => void
}

const ScopeBadges = ({
  scopes,
  color,
}: {
  scopes: string[]
  color?: string
}) => (
  <>
    {scopes.map((scope) => (
      <Badge key={scope} size="sm" variant="light" color={color}>
        {scope}
      </Badge>
    ))}
  </>
)

const ChangeLine = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-wrap items-center gap-1 text-sm text-doubleNickel-gray-900">
    {children}
  </div>
)

const describeChange = (entry: HistoryEntry) => {
  switch (entry.changeType) {
    case 'CREATE':
      return (
        <ChangeLine>
          <span>Created with scopes</span>
          <ScopeBadges scopes={entry.newScopes} />
        </ChangeLine>
      )
    case 'DELETE':
      return (
        <ChangeLine>
          <span>Deleted; had scopes</span>
          <ScopeBadges scopes={entry.prevScopes} />
        </ChangeLine>
      )
    default: {
      const added = entry.newScopes.filter(
        (scope) => !entry.prevScopes.includes(scope)
      )
      const removed = entry.prevScopes.filter(
        (scope) => !entry.newScopes.includes(scope)
      )
      return (
        <div className="flex flex-col gap-1">
          {added.length > 0 && (
            <ChangeLine>
              <span>Added scopes</span>
              <ScopeBadges scopes={added} />
            </ChangeLine>
          )}
          {removed.length > 0 && (
            <ChangeLine>
              <span>Removed scopes</span>
              <ScopeBadges scopes={removed} color="red" />
            </ChangeLine>
          )}
          {entry.newActive !== null && (
            <ChangeLine>
              <span>Set {entry.newActive ? 'active' : 'inactive'}</span>
            </ChangeLine>
          )}
        </div>
      )
    }
  }
}

const ApiClientAccessHistoryDrawer = ({
  row,
  onClose,
}: ApiClientAccessHistoryDrawerProps) => {
  const { data, loading, error } = useQuery(GET_API_CLIENT_ACCESS_HISTORY, {
    variables: {
      clientId: row?.clientId,
      companyId: row?.company.companyId,
    },
    skip: !row,
    fetchPolicy: 'network-only',
  })

  const entries: HistoryEntry[] = data?.apiClientAccessHistory ?? []

  return (
    <DnDrawer
      opened={!!row}
      onClose={onClose}
      stackId="api-client-access-history-drawer"
    >
      <div className="flex h-screen flex-col gap-6 p-6 pb-14">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold text-doubleNickel-gray-900">
            History
          </h2>
          {row && (
            <p className="text-sm text-doubleNickel-gray-600">
              {row.clientName || row.clientId} · {row.company.companyName}
            </p>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {loading && (
            <div className="flex flex-1 items-center justify-center">
              <Loader size="sm" color="gray" />
            </div>
          )}

          {error && (
            <p className="text-sm text-doubleNickel-error-600">
              Could not load history: {error.message}
            </p>
          )}

          {!loading && !error && entries.length === 0 && (
            <p className="text-sm text-doubleNickel-gray-500">
              No history yet.
            </p>
          )}

          {!loading && !error && entries.length > 0 && (
            <Timeline
              bulletSize={12}
              lineWidth={2}
              classNames={{ itemTitle: 'h-0' }}
            >
              {entries.map((entry) => (
                <Timeline.Item key={entry.auditTrailId}>
                  <div className="flex flex-col gap-1">
                    {describeChange(entry)}
                    <div className="flex flex-wrap gap-1 text-xs text-doubleNickel-gray-500">
                      <span>
                        {dayjs(entry.createdAt).format('MMM D, YYYY HH:mm')}
                      </span>
                      <span>·</span>
                      <span className="font-medium">
                        {entry.user
                          ? `${entry.user.firstName} ${entry.user.lastName}`
                          : 'API traffic'}
                      </span>
                    </div>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          )}
        </div>
      </div>
    </DnDrawer>
  )
}

export default ApiClientAccessHistoryDrawer
